import "isomorphic-fetch";

import memoize from "memoizee";

import monolith from "../../../utils/fetch/fetches";
import {
  BookAppointmentRequest,
  BookAppointmentResponse,
  BookedAppointment,
  DefaultFields,
  RescheduleAppointmentRequest,
  RescheduledAppointmentResponse,
  isRescheduleRequest,
} from "./types";

/**
 * Cancel an appointment.
 *
 * @return A Promise which resolves if the appointment was canceled and rejects on errors.
 */
export const cancelAppointment = memoize(
  async (id: string, rescheduling = false): Promise<Response> => {
    return monolith()(`/hib/appointments/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify({rescheduling}),
    });
  },
  {
    promise: true,
  },
);

const defaults: DefaultFields = {
  instant: false,
  titl: "Appointment",
};

export const bookAppointment = memoize(
  async (request: BookAppointmentRequest): Promise<BookedAppointment> => {
    const data = {
      ...defaults,
      ...request,
    };

    const response = await monolith()("/hib/appointments/withValidation", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return await response.json();
  },
  {
    promise: true,
    normalizer: JSON.stringify,
  },
);

/**
 * If the `reschedulingApptId` is present in the request, this function will cancel the appointment and book a new one.
 * Otherwise, it will just book a new appointment.
 */
export async function bookOrReschedule<
  T extends BookAppointmentRequest | RescheduleAppointmentRequest,
>(
  request: T,
): Promise<
  T extends RescheduleAppointmentRequest ? RescheduledAppointmentResponse : BookAppointmentResponse
> {
  if (isRescheduleRequest(request)) {
    return rescheduleAppointment(request);
  }

  try {
    return {
      booking: await bookAppointment(request),
      bookingError: undefined,
    };
  } catch (error) {
    return {
      booking: undefined,
      bookingError: error,
    };
  }
}

/**
 * Reschedule an existing appointment.
 *
 * NOTE: This Promise _always_ resolves. That does not necessarily mean the operation was a success.
 * Callers need to check if the resolved value's "booking" property was defined.
 *
 * If "resolvedValue.bookingError" is defined, something went wrong and no changes were made
 * to the server.
 *
 * If "resolvedValue.booking" is defined, a new appointment was booked successfully, but this
 * does not necessarily mean the old one was canceled.
 *
 * If "resolvedValue.cancelled" is defined, then the old appointment was cancelled as well.
 *
 * The old appointment will never be cancelled before the new one gets booked.
 *
 * @param request The new appointment request.
 */
export async function rescheduleAppointment(
  request: RescheduleAppointmentRequest,
): Promise<RescheduledAppointmentResponse> {
  const [booking] = await Promise.allSettled([bookAppointment(request)]);
  if (booking.status === "rejected") {
    return {
      booking: undefined,
      bookingError: booking.reason,
      cancelledError: new Error(
        "The appointment was not cancelled because the new one failed to book.",
      ),
    };
  }
  const [cancelled] = await Promise.allSettled([
    cancelAppointment(request.reschedulingApptId, true),
  ]);

  return {
    booking: booking.value,
    cancelled: cancelled.status === "fulfilled" ? cancelled.value : undefined,
    bookingError: undefined,
    cancelledError: cancelled.status === "rejected" ? cancelled.reason : undefined,
  };
}
